import Modal from "../../General/ConfirmableModal";
import React, { useState } from "react";
import axios from "axios";
import {
  Button,
  Box,
  Typography,
  IconButton,
  TextField,
} from "@mui/material";
import { ContentCopy } from "@mui/icons-material";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import { useQueryClient } from "@tanstack/react-query";

export interface RelayCodeRequest {
  constraint_amount: number;
  constraint_organization_id?: string;
  constraint_location_id?: string;
  constraint_deadline?: string;
  note?: string;
  po_numbers?: string[];
  company?: string;
  contact_phone?: string;
  dest_name?: string;
  dest_site_id?: string;
  dest_address?: string;
  dest_city?: string;
  dest_state?: string;
  dest_zip?: string;
}

const CreateRelayCodeButton = ({ company }: { company: string }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [response, setResponse] = useState<any>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [amount, setAmount] = useState<string>("");
  const [note, setNote] = useState<string>("");
  const [webhookResponse, setWebhookResponse] = useState<any>(null);
  const { token } = useAuth();
  const queryClient = useQueryClient();

  const createRelayCode = async (data: RelayCodeRequest) => {
    setLoading(true);
    setError(null);

    try {
      const result = await axios.post(`${apiVariable}/relayCode`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          company: company,
        },
      });

      setResponse(result.data);
      setModalOpen(true);
      queryClient.invalidateQueries({ queryKey: ["relayCodes", company] });
    } catch (err: any) {
      setError(err.response?.data?.message || "An error occurred.");
    } finally {
      setLoading(false);
    }
  };

  const subscribeWebhook = async () => {
    setLoading(true);
    setError(null);

    try {
      const subscriptionData = {
        url: `${apiVariable}/handleRelaySubscription`,
        category: "charge_receipt",
      };

      const result = await axios.post(
        `${apiVariable}/subscribeWebhook`,
        subscriptionData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setWebhookResponse(result.data);
      alert("Webhook subscribed successfully!");
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          "An error occurred while subscribing to the webhook."
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePopupSubmit = () => {
    const relayCodeData: RelayCodeRequest = {
      constraint_amount: Number(parseFloat(amount).toFixed(2)) * 100,
      note,
    };

    createRelayCode(relayCodeData);
    setPopupOpen(false);
  };

  const handleCopyCode = () => {
    if (response?.code) {
      navigator.clipboard.writeText(response.code);
      alert("Code copied to clipboard!");
    }
  };

  return (
    <div>
      <Button
        variant="contained"
        color="primary"
        onClick={() => setPopupOpen(true)}
        disabled={loading}
      >
        {loading ? "Creating Relay Code..." : "Create Relay Code"}
      </Button>

      {/* <Button
        variant="outlined"
        color="secondary"
        onClick={subscribeWebhook}
        disabled={loading}
        sx={{ ml: 2 }}
      >
        {loading ? "Subscribing..." : "Subscribe to Webhook"}
      </Button> */}

      {error && <Typography color="error">{error}</Typography>}

      <Modal open={popupOpen} onClose={() => setPopupOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            borderRadius: "8px",
            boxShadow: 24,
            p: 4,
            textAlign: "center",
          }}
        >
          <Typography variant="h6">Enter Relay Code Details</Typography>
          <TextField
            label="Amount (in dollars)"
            type="number"
            fullWidth
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            sx={{ mt: 2 }}
          />
          <TextField
            label="Note"
            type="text"
            fullWidth
            value={note}
            onChange={(e) => setNote(e.target.value)}
            sx={{ mt: 2 }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handlePopupSubmit}
            disabled={!amount || !note || loading}
            sx={{ mt: 2 }}
          >
            Submit
          </Button>
        </Box>
      </Modal>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            borderRadius: "8px",
            boxShadow: 24,
            p: 4,
            textAlign: "center",
          }}
        >
          <Typography variant="h6">Relay Code Created Successfully</Typography>
          {response?.code && (
            <Typography
              variant="body1"
              sx={{
                mt: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <span style={{ marginRight: 8 }}>{response.code}</span>
              <IconButton onClick={handleCopyCode}>
                <ContentCopy />
              </IconButton>
            </Typography>
          )}
          <Button
            variant="contained"
            color="secondary"
            onClick={() => setModalOpen(false)}
            sx={{ mt: 2 }}
          >
            Close
          </Button>
        </Box>
      </Modal>
    </div>
  );
};

export default CreateRelayCodeButton;
